import React, { useState, useEffect, useMemo } from "react";
import { Dialog } from "@headlessui/react";
import { XMarkIcon, BoltIcon } from "@heroicons/react/24/outline";
import { useBook } from "../../../contexts/BookContext";
import { getCloudFunctionUrl } from "../../../config/firebaseFunctionUrl";
import { ensureDesignPrompts, ensureDesignPromptsCheck, updateSingleElementPrompt } from '../../../services/bookService/operations/ensureDesignPrompts';
import { useCredits } from "../../../contexts/CreditsContext";
import { updateBook } from "../../../services/firebase/firestore";
import { visualStyles } from "../../../data/styleOptions";
import { db } from "../../../config/firebase";
import { doc, getDoc, setDoc, onSnapshot } from "firebase/firestore";
import { auth } from "../../../config/firebase";
import StyleDropdown from "../components/StyleDropdown";
import {
  buildPendingJobKeySet,
  calculateMissingElements,
  collectMissingImageElements,
  collectOverwritableImageElements,
} from "./generateAllImagesLogic";

const GENERATION_COST = 5;
const GENERATION_STATES = {
  PREPARING: {
    text: "Preparing your book for image generation... Please wait, don't close this window or refresh the page.",
    progress: 25,
  },
  GENERATING: {
    text: "Generating images... Please wait, don't close this window or refresh the page.",
    progress: 50,
  },
  SAVING: {
    text: "Almost there... Please wait, don't close this window or refresh the page.",
    progress: 75,
  },
  COMPLETING: {
    text: "Completing the process... Please wait, don't close this window or refresh the page.",
    progress: 100,
  },
};

export default function GenerateAllImagesModal({
  isOpen,
  onClose,
  onConfirm,
  isGenerating,
}) {
  const { currentBook, loadBook, pendingImageJobs, pendingImageJobsLoaded } = useBook();
  const { checkCredits } = useCredits();

  // Set of spreadId:elementId keys for active jobs from BookContext
  const pendingJobKeySet = useMemo(
    () => buildPendingJobKeySet(pendingImageJobs),
    [pendingImageJobs]
  );

  // Context for the shared classification helpers in generateAllImagesLogic
  const jobCtx = useMemo(
    () => ({ pendingJobKeySet, pendingImageJobs, pendingImageJobsLoaded }),
    [pendingJobKeySet, pendingImageJobs, pendingImageJobsLoaded]
  );

  const [isGeneratingLocally, setIsGeneratingLocally] = useState(false);
  const [currentState, setCurrentState] = useState("PREPARING");
  const [progress, setProgress] = useState(0);
  const [overwriteElementsCount, setOverwriteElementsCount] = useState(0);
  const [missingElementsCount, setMissingElementsCount] = useState(0);
  const [elementsWithoutPromptsCount, setElementsWithoutPromptsCount] = useState(0);
  const [selectedStyleId, setSelectedStyleId] = useState(null);
  const [coverOrientation, setCoverOrientation] = useState("landscape_4_3");
  const [interiorOrientation, setInteriorOrientation] = useState("portrait_4_3");
  const [needsPromptUpdate, setNeedsPromptUpdate] = useState(true);
  const [promptUpdateError, setPromptUpdateError] = useState("");
  const [isUpdatingPrompts, setIsUpdatingPrompts] = useState(false);
  const [isGeneratingMissingPrompts, setIsGeneratingMissingPrompts] = useState(false);
  // Latest book data from the modal's own real-time listener. currentBook can be
  // stale for the spread currently being edited (BookContext skips merging it),
  // so counts and queue lists must prefer this.
  const [liveBookData, setLiveBookData] = useState(null);

  // Helper: Queue jobs with spread-level concurrency (Wave 1: one per spread, Wave 2+: sequential within spread)
  const queueJobsWithSpreadConcurrency = async (elementsToGenerate, worker) => {
    if (!Array.isArray(elementsToGenerate) || elementsToGenerate.length === 0) return [];

    // Group elements by spread
    const elementsBySpread = elementsToGenerate.reduce((acc, el) => {
      const spreadId = el.spreadId;
      if (!acc[spreadId]) acc[spreadId] = [];
      acc[spreadId].push(el);
      return acc;
    }, {});

    const allQueuedJobs = [];
    const spreadIds = Object.keys(elementsBySpread);

    // Wave 1: Queue first element from each spread concurrently
    console.log('[GenerateAllImagesModal] Wave 1: Queueing first element from each spread');
    const wave1Jobs = await Promise.all(
      spreadIds.map(async (spreadId) => {
        const elements = elementsBySpread[spreadId];
        if (elements.length === 0) return null;
        try {
          return await worker(elements[0]);
        } catch (err) {
          console.error(`[GenerateAllImagesModal] Wave 1 failed for spread ${spreadId}:`, err);
          throw err;
        }
      })
    );
    allQueuedJobs.push(...wave1Jobs.filter(Boolean));

    // Wave 2+: For each spread, queue remaining elements sequentially (but spreads in parallel)
    if (spreadIds.some(sid => elementsBySpread[sid].length > 1)) {
      console.log('[GenerateAllImagesModal] Wave 2+: Queueing remaining elements sequentially per spread');
      await Promise.all(
        spreadIds.map(async (spreadId) => {
          const elements = elementsBySpread[spreadId];
          // Process remaining elements (skip first one already queued in Wave 1)
          for (let i = 1; i < elements.length; i++) {
            try {
              const job = await worker(elements[i]);
              if (job) allQueuedJobs.push(job);
            } catch (err) {
              console.error(`[GenerateAllImagesModal] Wave 2+ failed for spread ${spreadId} element ${i}:`, err);
              throw err;
            }
          }
        })
      );
    }

    return allQueuedJobs;
  };

  const updateSpreadSubdocsWithPendingJobs = async ({ queuedJobs, artStyle, styleId }) => {
    if (!currentBook?.id) return;
    if (!Array.isArray(queuedJobs) || queuedJobs.length === 0) return;

    // Group jobs by spread to minimize reads/writes
    const jobsBySpread = queuedJobs.reduce((acc, j) => {
      if (!j?.spreadId || !j?.elementId) return acc;
      if (!acc[j.spreadId]) acc[j.spreadId] = [];
      acc[j.spreadId].push(j);
      return acc;
    }, {});

    await Promise.all(
      Object.entries(jobsBySpread).map(async ([spreadId, jobs]) => {
        const spreadRef = doc(db, "books", currentBook.id, "spreads", spreadId);
        const snap = await getDoc(spreadRef);
        if (!snap.exists()) return;
        const spread = snap.data() || {};
        const arr = Array.isArray(spread.elements) ? spread.elements : [];
        if (arr.length === 0) return;

        const jobMap = jobs.reduce((m, j) => {
          m[j.elementId] = j;
          return m;
        }, {});
        // For elements without id/docId, the array index is the canonical
        // elementKey used when the job was queued
        const jobByIndex = jobs.reduce((m, j) => {
          if (j.elementKey != null) m[String(j.elementKey)] = j;
          return m;
        }, {});

        const nextElements = arr.map((el, idx) => {
          const eid = el?.id || el?.docId;
          const job = (eid && jobMap[eid]) || jobByIndex[String(idx)] || null;
          if (!job) return el;
          return {
            ...el,
            // keep subdoc element id aligned with the canonical job elementId
            id: job.elementId || eid || String(idx),
            visualStyle: artStyle,
            visualStyleId: styleId,
            imageGenerationStatus: "pending",
            imageUpdateJobId: job.jobId || null,
          };
        });

        await setDoc(
          spreadRef,
          {
            elements: nextElements,
            updatedAt: new Date(),
          },
          { merge: true }
        );
      })
    );
  };

  // Check if prompts need updating
  const checkIfPromptsNeedUpdate = async () => {
    if (!currentBook?.id) {
      setNeedsPromptUpdate(false);
      return;
    }

    try {
      const result = await ensureDesignPromptsCheck(currentBook.id);
      console.log("Prompts check result:", result);
      
      // Show button if not skipped (i.e., if updates are needed)
      setNeedsPromptUpdate(!(result && typeof result === 'object' && 'skipped' in result && result.skipped));
      setPromptUpdateError("");
    } catch (error) {
      console.error("Error checking prompts:", error);
      // Default to showing button if check fails
      setNeedsPromptUpdate(true);
      setPromptUpdateError("");
    }
  };

  // Initialize the style selection when modal opens
  useEffect(() => {
    if (isOpen) {
      const defaultId = currentBook?.visualStyle?.id || "digital-scifi";
      setSelectedStyleId((prev) => prev ?? defaultId);
    }
  }, [isOpen, currentBook]);

  // Calculate the elements to generate on modal open and whenever book data or active jobs change.
  // Prefer liveBookData (real-time book listener) over currentBook — the active
  // spread can be stale in context state.
  useEffect(() => {
    if (isOpen && currentBook) {
      const bookForCounts = liveBookData || currentBook;
      const { missingElements, elementsWithoutPrompts, overwriteElements } = calculateMissingElements(bookForCounts, jobCtx);

      setOverwriteElementsCount(overwriteElements.length);
      setMissingElementsCount(missingElements.length);
      setElementsWithoutPromptsCount(elementsWithoutPrompts.length);

      // Check if prompts need updating
      checkIfPromptsNeedUpdate();
    }
  }, [isOpen, currentBook, liveBookData, jobCtx]);

  // Set up real-time listener for book changes to catch Firestore updates
  useEffect(() => {
    if (!isOpen || !currentBook?.id) {
      setLiveBookData(null);
      return;
    }

    console.log('[GenerateAllImagesModal] Setting up real-time listener for book:', currentBook.id);
    
    const unsubscribe = onSnapshot(
      doc(db, 'books', currentBook.id),
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const bookData = docSnapshot.data();
          console.log('[GenerateAllImagesModal] Real-time book update received');
          // Store the freshest book data; the count effect recalculates from it
          setLiveBookData(bookData);
        }
      },
      (error) => {
        console.error('[GenerateAllImagesModal] Real-time listener error:', error);
      }
    );

    return () => unsubscribe();
  }, [isOpen, currentBook?.id]);

  const handleUpdateAllPrompts = async () => {
    if (!currentBook?.id) {
      setPromptUpdateError("Book not found");
      return;
    }

    setIsUpdatingPrompts(true);
    try {
      setPromptUpdateError("");
      console.log("Updating all image prompts for book:", currentBook.id);
      
      const result = await ensureDesignPrompts(currentBook.id);
      console.log("Update all prompts result:", result);
      
      if (result && typeof result === 'object' && 'success' in result && result.success) {
        // Refresh book data to show updated prompts without page reload
        if (currentBook?.id) {
          loadBook(currentBook.id);
        }
        // Re-check prompts status
        await checkIfPromptsNeedUpdate();
      } else {
        const errorMsg = result && typeof result === 'object' && 'error' in result ? String(result.error) : "Failed to update image prompts";
        setPromptUpdateError(errorMsg);
      }
    } catch (error) {
      console.error("Error updating all prompts:", error);
      setPromptUpdateError("Failed to update image prompts. Please try again.");
    } finally {
      setIsUpdatingPrompts(false);
    }
  };

  const handleGenerateMissingPrompts = async () => {
    if (!currentBook?.id) {
      setPromptUpdateError("Book not found");
      return;
    }

    setIsGeneratingMissingPrompts(true);
    try {
      setPromptUpdateError("");
      
      // Find all image elements without prompts (prefer live snapshot — see above)
      const elementsWithoutPrompts = [];
      Object.entries(liveBookData?.spreads || currentBook.spreads || {}).forEach(([spreadId, spread]) => {
        if (!Array.isArray(spread.elements)) return;
        spread.elements.forEach((element, idx) => {
          const hasPrompts = (Array.isArray(element.imagePrompts) && element.imagePrompts.length > 0) || !!element.imagePrompt;
          if (element.type === 'image' && !hasPrompts) {
            elementsWithoutPrompts.push({ spreadId, elementId: element.id || element.docId, element });
          }
        });
      });

      // Generate all missing prompts concurrently
      await Promise.all(
        elementsWithoutPrompts.map(({ spreadId, elementId }) =>
          updateSingleElementPrompt(currentBook.id, spreadId, elementId)
        )
      );

      // Reload book to show updated prompts
      if (currentBook?.id) {
        loadBook(currentBook.id);
      }
    } catch (error) {
      console.error("Error generating missing prompts:", error);
      setPromptUpdateError("Failed to generate prompts. Please try again.");
    } finally {
      setIsGeneratingMissingPrompts(false);
    }
  };

  const generateAllImages = async () => {
    if (!currentBook) return;

    setIsGeneratingLocally(true);
    setCurrentState("PREPARING");
    setProgress(GENERATION_STATES.PREPARING.progress);

    try {
      // Get all overwritable image elements from all spreads.
      // Prefer the modal's live book snapshot — currentBook.spreads can be stale
      // for the spread currently being edited (BookContext skips merging it).
      const spreads = liveBookData?.spreads || currentBook.spreads;
      const elementsToGenerate = collectOverwritableImageElements(spreads, jobCtx);
      console.log('GenerateAllImagesModal: elements to overwrite', elementsToGenerate.map(el => ({
        spreadId: el.spreadId,
        elementId: el.elementId,
      })));

      // Calculate total cost (5 credits per image)
      const totalCost = elementsToGenerate.length * GENERATION_COST;

      // Check if user has enough credits
      const hasCredits = await checkCredits(totalCost);
      if (!hasCredits) {
        alert(
          "Insufficient credits. You need " +
            totalCost +
            " credits to generate all images."
        );
        setIsGeneratingLocally(false);
        return;
      }

      const user = auth?.currentUser;
      if (!user) {
        alert('You must be signed in to generate images.');
        setIsGeneratingLocally(false);
        return;
      }
      const token = await user.getIdToken();

      // Start generation process
      onConfirm(); // This will set isGenerating to true

      try {
        setCurrentState("GENERATING");
        setProgress(GENERATION_STATES.GENERATING.progress);

        const styleFromSelection = visualStyles.find((s) => s.id === selectedStyleId);
        const artStyle =
          styleFromSelection?.value ||
          currentBook?.visualStyle?.value ||
          "Digital SciFi Art";
        const styleId =
          styleFromSelection?.id || currentBook?.visualStyle?.id || null;

        const callbackBase =
          getCloudFunctionUrl('updateImageElement');

        // Queue all images with spread-level concurrency (webhook will update elements when done)
        const queuedJobs = await queueJobsWithSpreadConcurrency(
          elementsToGenerate,
          async (element) => {
            try {
              console.log(
                `Queueing image generation for element ${element.elementId}...`,
                element.imagePrompt
              );
              
              // If imagePrompt is missing but imagePrompts array exists, use the latest (last) prompt
              let promptToUse = element.imagePrompt;
              let promptIndex = 0;
              if (!promptToUse && Array.isArray(element.imagePrompts) && element.imagePrompts.length > 0) {
                promptToUse = element.imagePrompts[element.imagePrompts.length - 1]; // Use latest prompt
                promptIndex = element.imagePrompts.length - 1;
                console.log('Using latest prompt from imagePrompts array:', {
                  elementId: element.elementId,
                  spreadId: element.spreadId,
                  promptIndex,
                  promptPreview: promptToUse.slice(0, 100),
                });
              }

              const cbBookId = currentBook?.id;
              const cbSpreadId = element?.spreadId;
              const cbElementId = element?.elementId;
              if (!cbBookId || !cbSpreadId || !cbElementId) {
                throw new Error("Missing identifiers to create image job");
              }

              const elementIsCover = cbSpreadId === 'spread-0';
              const orientation = elementIsCover ? coverOrientation : interiorOrientation;

              const jobResp = await fetch(
                getCloudFunctionUrl('createImageUpdateJob'),
                {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                  },
                  body: JSON.stringify({
                    bookId: cbBookId,
                    elementId: cbElementId,
                    spreadId: cbSpreadId,
                    costCredits: GENERATION_COST,
                    orientation: orientation,
                    position: element?.position || null,
                    promptIndex: promptIndex,
                    prompt: promptToUse,
                    artStyle: artStyle,
                    bookContext: currentBook,
                  }),
                }
              );

              const jobJson = await jobResp.json().catch(() => ({}));
              if (!jobResp.ok || !jobJson?.jobId) {
                if (jobResp.status === 402) {
                  throw new Error(jobJson?.error || 'Insufficient credits');
                }
                throw new Error(jobJson?.error || "Failed to create image job");
              }

              return {
                spreadId: cbSpreadId,
                elementId: cbElementId,
                elementKey: element?.elementKey,
                jobId: jobJson.jobId,
              };
            } catch (error) {
              console.error(
                `Error queueing element ${element.elementId}:`,
                error
              );
              throw error;
            }
          }
        );

        // Persist chosen visual style to elements (images will arrive via webhook)
        setCurrentState("SAVING");
        setProgress(GENERATION_STATES.SAVING.progress);

        // Create a copy of the spreads to update (built from the same fresh data
        // used for queueing so keys stay consistent)
        const updatedSpreads = { ...spreads };

        queuedJobs.forEach(({ spreadId, elementKey, elementId, jobId }) => {
          if (!spreadId || !elementKey) return;
          const spread = updatedSpreads[spreadId];
          if (spread && spread.elements) {
            const elements = Array.isArray(spread.elements)
              ? spread.elements.slice()
              : { ...spread.elements };
            elements[elementKey] = {
              ...elements[elementKey],
              visualStyle: artStyle,
              visualStyleId: styleId,
              imageGenerationStatus: "pending",
              imageUpdateJobId: jobId || null,
              // keep canonical id in sync if it differs from the map key
              id: elementId,
            };
            updatedSpreads[spreadId] = { ...spread, elements };
          }
        });

        // Also update spread subdocuments because the Design canvas prefers subdocs
        await updateSpreadSubdocsWithPendingJobs({ queuedJobs, artStyle, styleId });

        // Update the book document with all changes at once
        await updateBook(currentBook.id, {
          ...currentBook,
          spreads: updatedSpreads,
        });

        setCurrentState("COMPLETING");
        setProgress(GENERATION_STATES.COMPLETING.progress);

        // Reload the book (images will populate as webhooks write element updates)
        await loadBook(currentBook.id);

        setTimeout(() => {
          setIsGeneratingLocally(false);
          onClose();
        }, 1000);
      } catch (error) {
        console.error("Error generating images:", error);
        alert("An error occurred while generating images. Please try again.");
        setIsGeneratingLocally(false);
      }
    } catch (error) {
      console.error("Error in generate all process:", error);
      alert("An error occurred while generating images. Please try again.");
      setIsGeneratingLocally(false);
    }
  };

  const generateMissingImages = async () => {
    if (!currentBook) return;

    setIsGeneratingLocally(true);
    setCurrentState("PREPARING");
    setProgress(GENERATION_STATES.PREPARING.progress);

    try {
      // Get all image elements with no usable src from all spreads (prompt not required).
      // Prefer the modal's live book snapshot — currentBook.spreads can be stale
      // for the spread currently being edited (BookContext skips merging it).
      const spreads = liveBookData?.spreads || currentBook.spreads;
      const elementsToGenerate = collectMissingImageElements(spreads, jobCtx);
      // Track which elements should be updated after jobs are queued
      const missingElementsMap = elementsToGenerate.reduce((acc, el) => {
        if (!acc[el.spreadId]) acc[el.spreadId] = {};
        acc[el.spreadId][el.elementKey] = true;
        return acc;
      }, {});

      if (elementsToGenerate.length === 0) {
        alert("No missing images found in your book.");
        setIsGeneratingLocally(false);
        return;
      }

      // Calculate total cost (5 credits per image)
      const totalCost = elementsToGenerate.length * GENERATION_COST;

      // Check if user has enough credits
      const hasCredits = await checkCredits(totalCost);
      if (!hasCredits) {
        alert(
          "Insufficient credits. You need " + totalCost + " credits to generate missing images."
        );
        setIsGeneratingLocally(false);
        return;
      }

      const user = auth?.currentUser;
      if (!user) {
        alert('You must be signed in to generate images.');
        setIsGeneratingLocally(false);
        return;
      }
      const token = await user.getIdToken();

      // Start generation process
      onConfirm(); // This will set isGenerating to true
      try {
        setCurrentState("GENERATING");
        setProgress(GENERATION_STATES.GENERATING.progress);

        const styleFromSelection = visualStyles.find((s) => s.id === selectedStyleId);
        const artStyle =
          styleFromSelection?.value ||
          currentBook?.visualStyle?.value ||
          "Digital SciFi Art";
        const styleId =
          styleFromSelection?.id || currentBook?.visualStyle?.id || null;

        const callbackBase =
          getCloudFunctionUrl('updateImageElement');

        // Generate any missing prompts concurrently first so every image can be queued
        // using the same spread-level concurrency path as images that already have prompts.
        const elementsNeedingPrompts = elementsToGenerate.filter((element) => {
          const hasPrompts = (Array.isArray(element.imagePrompts) && element.imagePrompts.length > 0) || !!element.imagePrompt;
          return !hasPrompts;
        });

        if (elementsNeedingPrompts.length > 0) {
          console.log('[GenerateAllImagesModal] Generating prompts concurrently for', elementsNeedingPrompts.length, 'missing images');
          await Promise.all(
            elementsNeedingPrompts.map(async (element) => {
              const promptResult = /** @type { { success: boolean | undefined, newPrompt: string | undefined, allPrompts: string[] | undefined, error: string | undefined } | undefined } */ (await updateSingleElementPrompt(
                currentBook?.id,
                element?.spreadId,
                element?.elementId
              ));
              if (!promptResult?.success || !promptResult.newPrompt) {
                throw new Error(
                  promptResult?.error ||
                  `Failed to generate prompt for element ${element.elementId}`
                );
              }
              element.imagePrompt = promptResult.newPrompt;
              element.imagePrompts = promptResult.allPrompts || [promptResult.newPrompt];
              console.log(
                `[GenerateAllImagesModal] Generated missing prompt for ${element.elementId}:`,
                promptResult.newPrompt.slice(0, 100)
              );
            })
          );
        }

        // Queue all missing images with spread-level concurrency (webhook will update elements when done)
        const queuedJobs = await queueJobsWithSpreadConcurrency(
          elementsToGenerate,
          async (element) => {
            try {
              console.log(
                `Queueing image generation for element ${element.elementId}...`,
                element.imagePrompt
              );

              let promptToUse = element.imagePrompt;
              let promptIndex = 0;

              if (!promptToUse && Array.isArray(element.imagePrompts) && element.imagePrompts.length > 0) {
                promptToUse = element.imagePrompts[element.imagePrompts.length - 1];
                promptIndex = element.imagePrompts.length - 1;
              }

              const cbBookId = currentBook?.id;
              const cbSpreadId = element?.spreadId;
              const cbElementId = element?.elementId;
              if (!cbBookId || !cbSpreadId || !cbElementId) {
                throw new Error("Missing identifiers to create image job");
              }

              const elementIsCover = cbSpreadId === 'spread-0';
              const orientation = elementIsCover ? coverOrientation : interiorOrientation;

              const jobResp = await fetch(
                getCloudFunctionUrl('createImageUpdateJob'),
                {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                  },
                  body: JSON.stringify({
                    bookId: cbBookId,
                    elementId: cbElementId,
                    spreadId: cbSpreadId,
                    costCredits: GENERATION_COST,
                    orientation: orientation,
                    position: element?.position || null,
                    promptIndex: promptIndex,
                    prompt: promptToUse,
                    artStyle: artStyle,
                    bookContext: currentBook,
                  }),
                }
              );
              const jobJson = await jobResp.json().catch(() => ({}));
              if (!jobResp.ok || !jobJson?.jobId) {
                if (jobResp.status === 402) {
                  throw new Error(jobJson?.error || 'Insufficient credits');
                }
                throw new Error(jobJson?.error || "Failed to create image job");
              }

              return {
                spreadId: cbSpreadId,
                elementId: cbElementId,
                elementKey: element?.elementKey,
                jobId: jobJson.jobId,
                imagePrompt: element.imagePrompt,
                imagePrompts: element.imagePrompts,
              };
            } catch (error) {
              console.error(
                `Error queueing element ${element.elementId}:`,
                error
              );
              throw error;
            }
          }
        );

        // Persist chosen visual style to elements (images will arrive via webhook)
        setCurrentState("SAVING");
        setProgress(GENERATION_STATES.SAVING.progress);

        // Create a copy of the spreads to update (built from the same fresh data
        // used for queueing so keys stay consistent)
        const updatedSpreads = { ...spreads };

        queuedJobs.forEach(({ spreadId, elementKey, elementId, jobId, imagePrompt, imagePrompts }) => {
          if (!spreadId || !elementKey) return;
          const spread = updatedSpreads[spreadId];
          if (
            missingElementsMap[spreadId] &&
            missingElementsMap[spreadId][elementKey] &&
            spread &&
            spread.elements
          ) {
            const elements = Array.isArray(spread.elements)
              ? spread.elements.slice()
              : { ...spread.elements };
            const nextElement = {
              ...elements[elementKey],
              visualStyle: artStyle,
              visualStyleId: styleId,
              imageGenerationStatus: "pending",
              imageUpdateJobId: jobId || null,
              // keep canonical id in sync if it differs from the map key
              id: elementId,
            };
            if (imagePrompt) nextElement.imagePrompt = imagePrompt;
            if (Array.isArray(imagePrompts)) nextElement.imagePrompts = imagePrompts;
            elements[elementKey] = nextElement;
            updatedSpreads[spreadId] = { ...spread, elements };
          }
        });

        // Also update spread subdocuments because the Design canvas prefers subdocs
        await updateSpreadSubdocsWithPendingJobs({ queuedJobs, artStyle, styleId });

        // Update the book document with all changes at once
        await updateBook(currentBook.id, {
          ...currentBook,
          spreads: updatedSpreads,
        });

        setCurrentState("COMPLETING");
        setProgress(GENERATION_STATES.COMPLETING.progress);

        // Reload the book to get fresh data
        await loadBook(currentBook.id);

        setTimeout(() => {
          setIsGeneratingLocally(false);
          onClose();
        }, 1000);
      } catch (error) {
        console.error("Error generating images:", error);
        alert("An error occurred while generating images. Please try again.");
        setIsGeneratingLocally(false);
      }
    } catch (error) {
      console.error("Error in generate missing images process:", error);
      alert("An error occurred while generating images. Please try again.");
      setIsGeneratingLocally(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      className="fixed inset-0 z-50 overflow-y-auto"
    >
      <div className="flex items-center justify-center min-h-screen p-4">
        <Dialog.Overlay className="fixed inset-0 bg-black/30" />

        <div className="relative bg-white rounded-lg max-w-4xl w-full p-6">
          <div className="absolute top-4 right-4">
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <div className="mt-2">
            <Dialog.Title className="text-lg font-medium text-gray-900 mb-6">
              Generate Images
            </Dialog.Title>

            {isGeneratingLocally || isGenerating ? (
              <div className="space-y-4">
                <div className="flex items-center justify-center">
                  <BoltIcon className="animate-pulse h-8 w-8 text-indigo-500" />
                </div>
                <p className="text-center text-gray-600">
                  {GENERATION_STATES[currentState].text}
                </p>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div
                    className="bg-indigo-600 h-2.5 rounded-full transition-all duration-500"
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
              </div>
            ) : (
              <>
                <p className="text-sm text-gray-500 mb-6">
                  Generating images will take some time. Please do not close
                  your browser during this process.
                </p>

                {/* Two Column Layout */}
                <div className="grid grid-cols-2 gap-6">
                  {/* Left Column: Image Generation Settings */}
                  <div className="space-y-4">
                    <h3 className="text-sm font-semibold text-gray-900 mb-4">Image Generation Settings</h3>
                    
                    {/* Visual Style Selector */}
                    <StyleDropdown
                      selectedStyleId={selectedStyleId}
                      onStyleChange={setSelectedStyleId}
                      label="Visual Style"
                    />

                    {/* Orientation Selectors - Side by Side */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Orientations
                      </label>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Cover Image
                          </label>
                          <select
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                            value={coverOrientation}
                            onChange={(e) => setCoverOrientation(e.target.value)}
                          >
                            <option value="square_hd">Square HD</option>
                            <option value="square">Square</option>
                            <option value="portrait_4_3">Portrait 3:4</option>
                            <option value="portrait_16_9">Portrait 9:16</option>
                            <option value="landscape_4_3">Landscape 4:3</option>
                            <option value="landscape_16_9">Landscape 16:9</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Interior Image
                          </label>
                          <select
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                            value={interiorOrientation}
                            onChange={(e) => setInteriorOrientation(e.target.value)}
                          >
                            <option value="square_hd">Square HD</option>
                            <option value="square">Square</option>
                            <option value="portrait_4_3">Portrait 3:4</option>
                            <option value="portrait_16_9">Portrait 9:16</option>
                            <option value="landscape_4_3">Landscape 4:3</option>
                            <option value="landscape_16_9">Landscape 16:9</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* Generation Buttons */}
                    <div className="space-y-3 pt-2">
                      {missingElementsCount > 0 && (
                        <button
                          onClick={generateMissingImages}
                          disabled={missingElementsCount === 0}
                          className={`w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white ${
                            missingElementsCount === 0
                              ? "bg-gray-400 cursor-not-allowed"
                              : "bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                          }`}
                        >
                          Generate Missing Images ({missingElementsCount}) - {missingElementsCount * GENERATION_COST} Credits Total
                        </button>
                      )}
                      {overwriteElementsCount > 0 && (
                        <button
                          onClick={generateAllImages}
                          disabled={overwriteElementsCount === 0}
                          className={`w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white ${
                            overwriteElementsCount === 0
                              ? "bg-gray-400 cursor-not-allowed"
                              : "bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                          }`}
                        >
                          Overwrite Existing Images ({overwriteElementsCount}) - {overwriteElementsCount * GENERATION_COST} Credits Total
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Right Column: Image Prompt Management */}
                  <div className="space-y-4">
                    <h3 className="text-sm font-semibold text-gray-900 mb-4">Image Prompt Management</h3>
                    
                    {/* Primary: Update Existing Prompts Section */}
                    <div className="space-y-3 p-4 bg-gradient-to-br from-indigo-50 to-indigo-50 border border-indigo-200 rounded-lg">
                      <div className="flex items-start gap-3">
                        <svg className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-indigo-900">Sync Prompts with Story Changes</p>
                          <p className="text-xs text-indigo-700 mt-1">
                            Update image prompts based on your latest world and character data
                          </p>
                        </div>
                      </div>
                      
                      {needsPromptUpdate ? (
                        <>
                          <button
                            onClick={handleUpdateAllPrompts}
                            disabled={isUpdatingPrompts}
                            className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-md font-medium transition-colors duration-200 ${
                              isUpdatingPrompts
                                ? 'bg-indigo-400 text-white cursor-not-allowed'
                                : 'bg-indigo-600 hover:bg-indigo-700 text-white'
                            }`}
                          >
                            {isUpdatingPrompts ? (
                              <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                            ) : (
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                              </svg>
                            )}
                            {isUpdatingPrompts ? 'Syncing...' : 'Sync All Prompts'}
                          </button>
                          {promptUpdateError && (
                            <div className="p-2.5 bg-red-50 text-red-700 rounded-md text-xs border border-red-200">
                              {promptUpdateError}
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 text-green-700 rounded-md">
                          <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <p className="text-xs font-medium">All prompts are synchronized</p>
                        </div>
                      )}
                    </div>

                    {/* Secondary: Generate Missing Prompts Section */}
                    {elementsWithoutPromptsCount > 0 && !isUpdatingPrompts && (
                      <div className="space-y-3 p-4 bg-gradient-to-br from-amber-50 to-amber-50 border border-amber-200 rounded-lg">
                        <div className="flex items-start gap-3">
                          <svg className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                          <div className="flex-1">
                            <p className="text-sm font-semibold text-amber-900">Create Prompts for New Elements</p>
                            <p className="text-xs text-amber-700 mt-1">
                              {elementsWithoutPromptsCount} image element{elementsWithoutPromptsCount !== 1 ? 's' : ''} {elementsWithoutPromptsCount !== 1 ? 'need' : 'needs'} prompt{elementsWithoutPromptsCount !== 1 ? 's' : ''}
                            </p>
                          </div>
                        </div>
                        
                        <button
                          onClick={handleGenerateMissingPrompts}
                          disabled={isGeneratingMissingPrompts || isUpdatingPrompts}
                          className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-md font-medium transition-colors duration-200 ${
                            isGeneratingMissingPrompts || isUpdatingPrompts
                              ? 'bg-amber-300 text-amber-900 cursor-not-allowed'
                              : 'bg-amber-500 hover:bg-amber-600 text-white'
                          }`}
                        >
                          {isGeneratingMissingPrompts ? (
                            <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                          ) : (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                          )}
                          {isGeneratingMissingPrompts ? 'Generating...' : `Generate Missing Prompts (${elementsWithoutPromptsCount})`}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </Dialog>
  );
}
